// Meeting Finder (Aaron via Jill, 2026-09-07): "when can the committee meet?"
// answered from the dispatch sheet, which already knows where every pilot
// will be. Pure helpers, node-testable (npm test); the page is
// src/pages/MeetingFinder.jsx.
//
// Jill's rulings (2026-09-07):
//   - Off duty = available. In port = available ("ports are usually
//     available"). Scenic cruising (GB, END, TA, HUB, MFJ) = MAYBE: the
//     pilots split the watches, so a member might be free. Travel = MAYBE:
//     early travel with a late call, or the reverse, can work.
//   - Underway with no port, or on a yacht = not available.
//   - Days are ranked in tiers, never a single yes/no: a seven-member board
//     almost never shares a port, so "everyone off duty" (a call) matters as
//     much as "everyone in Juneau" (in person).
//   - Only covers dates the dispatch sheet has been published for.

import { dayInfoFromRows, normPort } from "./whereIsWaldo.js";
import { periodForDate, onCallByPeriod } from "./winterDuty.js";
export { normPort };

// Access: board seats plus every committee position on the roster. A new
// official committee = one more roster position here (and in the roster's
// POSITION_OPTIONS + the pilot_positions check constraint).
export const BOARD_POSITIONS = ["president", "vice_president", "secretary", "member"];
export const TRAINING_POSITIONS = ["training_chair", "training_committee", "trainee_organizer", "deputy_organizer", "auditor", "ce_organizer"];
export const EXAM_POSITIONS = ["exam_committee"];
export const COMMITTEE_POSITIONS = [...TRAINING_POSITIONS, ...EXAM_POSITIONS];
// ce_lead is a legacy board_position value some rosters still carry.
export const MEETING_FINDER_POSITIONS = [...BOARD_POSITIONS, ...COMMITTEE_POSITIONS, "ce_lead"];

// Group presets. boardPositions match pilots_info.board_position; positions
// match pilot_positions rows. Presets PRE-CHECK members; the organizer sees
// every name and can drop or add people, so nobody is silently included.
export const GROUPS = [
  { value: "board", label: "Board of Directors", boardPositions: BOARD_POSITIONS, positions: [] },
  { value: "training", label: "Training Committee", boardPositions: ["training_chair", "ce_lead"], positions: TRAINING_POSITIONS },
  { value: "exam", label: "Exam Committee", boardPositions: [], positions: EXAM_POSITIONS },
  { value: "custom", label: "Hand-pick", boardPositions: [], positions: [] },
];

export function canUseMeetingFinder({ roles = [], boardPosition = null, committeePositions = [] } = {}) {
  const r = (roles || []).map((x) => String(x).toLowerCase());
  if (r.includes("system_admin")) return true;
  const held = [boardPosition, ...(committeePositions || [])].filter(Boolean).map((x) => String(x).toLowerCase());
  return held.some((h) => MEETING_FINDER_POSITIONS.includes(h));
}

// pilots: pilots_info rows; positionsById: { [pilot_id]: [position, ...] }.
// Active pilots only; inactive seats are stale roster data, not attendees.
export function groupMembers(groupValue, pilots, positionsById) {
  const g = GROUPS.find((x) => x.value === groupValue);
  if (!g) return [];
  return (pilots || [])
    .filter((p) => p.is_active !== false)
    .filter((p) => {
      const bp = String(p.board_position || "").toLowerCase();
      if (bp && g.boardPositions.includes(bp)) return true;
      const held = (positionsById?.[p.id] || []).map((x) => String(x).toLowerCase());
      return held.some((h) => g.positions.includes(h));
    })
    .map((p) => p.id);
}

// The scenic-cruising codes the dispatch sheet uses (domain memory: TA, END,
// GB, HUB, MFJ). Anything else in port_codes is a port.
export const SCENIC_CODES = ["TA", "END", "GB", "HUB", "MFJ"];
const SCENIC = new Set(SCENIC_CODES);

// "cruise" days the office files under TRAVEL (domain memory 2026-06-16).
const CRUISE_RE = /cruise|scenic|sea day|at sea/i;

// One pilot, one day -> { level, label }. level is one of:
//   off    available, home off duty
//   port   available, on a ship that is in port that day
//   maybe  scenic cruising, traveling, other duty, or not on the sheet
//   busy   underway with no port call, or on a yacht
// winter (optional): { onCall } when the date falls in a published winter
// duty week (Jill 2026-09-11). On dispatch = maybe (they can be called out); not
// on dispatch = off duty. On dispatch outranks a summer-sheet off day in the overlap
// weeks, but never a ship assignment.
export function dayAvailability(info, rawCells = [], winter = null) {
  if (!info) {
    if (winter) return winter.onCall ? { level: "maybe", label: "on dispatch this week" } : { level: "off", label: "off duty, not on dispatch" };
    return { level: "maybe", label: "not on the sheet" };
  }
  const st = info.status || "";
  const ship = info.vessel ? ` on ${info.vessel}` : "";
  if (st === "OFF_PAID" || st === "OFF_UNPAID") {
    if (winter?.onCall) return { level: "maybe", label: "on dispatch this week" };
    return { level: "off", label: "off duty" };
  }
  if (st === "TRAVEL") {
    if ((rawCells || []).some((c) => CRUISE_RE.test(String(c || "")))) return { level: "maybe", label: `scenic cruising${ship}` };
    return { level: "maybe", label: "traveling" };
  }
  if (st === "YACHT") return { level: "busy", label: "on a yacht" };
  if (st === "ASSIGNED") {
    const ports = [...(info.ports || [])].map(normPort).filter(Boolean);
    const scenic = ports.filter((p) => SCENIC.has(p));
    const real = ports.filter((p) => !SCENIC.has(p));
    if (!ports.length) return { level: "busy", label: `underway${ship}` };
    if (scenic.length) {
      const tail = real.length ? `, in port ${real.join("/")}` : "";
      return { level: "maybe", label: `scenic cruising ${scenic.join("/")}${tail}${ship}` };
    }
    return { level: "port", label: `in port ${real.join("/")}${ship}` };
  }
  return { level: "maybe", label: "other duty" };
}

// Where a pilot physically is that day, for the same-port tier: real ports
// from the sheet; an off day puts them at their home port. Scenic codes are
// not a meeting place.
export function locationsFor(info, homePort, winter = null) {
  const out = new Set();
  // on dispatch means at home, ready to be called out
  if (winter?.onCall && homePort && !(info && info.status === "ASSIGNED")) out.add(normPort(homePort));
  if (!info) return out;
  const st = info.status || "";
  for (const p of info.ports || []) { const n = normPort(p); if (n && !SCENIC.has(n)) out.add(n); }
  if ((st === "OFF_PAID" || st === "OFF_UNPAID") && homePort) out.add(normPort(homePort));
  return out;
}

export const TIERS = [
  { key: "together", label: "Everyone in the same port", hint: "In person is possible.", tone: "success" },
  { key: "all_off", label: "Everyone off duty", hint: "Best for a call.", tone: "success" },
  { key: "all_free", label: "Everyone off or in port", hint: "Good for a call.", tone: "info" },
  { key: "workable", label: "Workable", hint: "Someone is cruising, traveling, on dispatch, or not on the sheet. Check with them.", tone: "warn" },
  { key: "all_but_one", label: "All but one", hint: "One person is underway. Meet without them or brief them after.", tone: "neutral" },
];
export const tierFor = (key) => TIERS.find((t) => t.key === key) || null;

// members: [{ id, name, homePort, info, rawCells }] for ONE date.
// -> { date, tier, port, statuses, holdout }; tier null = not a meeting day.
export function rankDay(date, members) {
  const statuses = (members || []).map((m) => ({ id: m.id, name: m.name, ...dayAvailability(m.info, m.rawCells, m.winter) }));
  const busy = statuses.filter((s) => s.level === "busy");
  // A day nobody appears on is not a meeting day, it is a gap in the sheet.
  // A published winter week counts as appearing: it says who is on dispatch.
  const nobody = statuses.length > 0 && (members || []).every((m) => !m.info && !m.winter);
  if (!statuses.length || nobody || busy.length > 1) return { date, tier: null, statuses, port: null, holdout: null };
  if (busy.length === 1) return { date, tier: "all_but_one", statuses, port: null, holdout: busy[0] };

  let common = null;
  for (const m of members) {
    const locs = locationsFor(m.info, m.homePort, m.winter);
    common = common === null ? [...locs] : common.filter((p) => locs.has(p));
    if (!common.length) break;
  }
  if (common && common.length) return { date, tier: "together", statuses, port: common.sort().join("/"), holdout: null };
  if (statuses.every((s) => s.level === "off")) return { date, tier: "all_off", statuses, port: null, holdout: null };
  if (statuses.every((s) => s.level === "off" || s.level === "port")) return { date, tier: "all_free", statuses, port: null, holdout: null };
  return { date, tier: "workable", statuses, port: null, holdout: null };
}

// Inclusive ISO date list. Bounded so a typo (year 2062) cannot hang the page.
export function datesBetween(from, to, max = 400) {
  const out = [];
  if (!from || !to || to < from) return out;
  const [y, m, d] = from.split("-").map(Number);
  const cur = new Date(y, m - 1, d);
  while (out.length < max) {
    const iso = `${cur.getFullYear()}-${String(cur.getMonth() + 1).padStart(2, "0")}-${String(cur.getDate()).padStart(2, "0")}`;
    if (iso > to) break;
    out.push(iso);
    cur.setDate(cur.getDate() + 1);
  }
  return out;
}

// rows: this pilot's dispatch rows -> { [date]: [raw_cell, ...] } (for the
// cruise-under-TRAVEL wording check).
export function rawCellsByDate(rows) {
  const m = {};
  for (const r of rows || []) {
    const d = String(r.duty_date || "").slice(0, 10);
    if (!d) continue;
    (m[d] = m[d] || []).push(r.raw_cell || "");
  }
  return m;
}

// people: [{ id, name, homePort, rows }] -> ranked days over the date range,
// meeting days only, chronological. Also reports who never appears on the
// sheet in the range (over-nag: a missing pilot must not pass silently).
// sheetDates (optional): every date the published sheet has ANY row for; a
// date outside it is skipped rather than ranked as "nobody on the sheet".
// winter (optional): { periods, rows } from the winter duty tables. A date in
// a published winter week is ranked even when the summer sheet has no row for
// it, and nobody counts as "missing" there: the week says who is on dispatch.
export function rankDays(people, from, to, sheetDates = null, winter = null) {
  const prepared = (people || []).map((p) => ({
    id: p.id, name: p.name, homePort: p.homePort,
    info: dayInfoFromRows(p.rows), raw: rawCellsByDate(p.rows),
  }));
  const onSheet = sheetDates ? new Set(sheetDates) : null;
  const onCall = winter ? onCallByPeriod(winter.rows) : {};
  const days = [];
  let winterCovered = false;
  for (const date of datesBetween(from, to)) {
    const wp = winter ? periodForDate(winter.periods, date) : null;
    if (wp) winterCovered = true;
    if (onSheet && !onSheet.has(date) && !wp) continue;
    const members = prepared.map((p) => ({
      id: p.id, name: p.name, homePort: p.homePort, info: p.info[date], rawCells: p.raw[date],
      winter: wp ? { onCall: !!onCall[wp.period]?.has(p.id) } : null,
    }));
    const ranked = rankDay(date, members);
    if (ranked.tier) days.push(ranked);
  }
  const missing = winterCovered ? [] : prepared.filter((p) => !Object.keys(p.info).some((d) => d >= from && d <= to)).map((p) => p.name);
  return { days, missing };
}

const fmtDay = (iso) => {
  if (!iso) return "";
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
};

// Plain text for an email or group text.
export function summaryText({ groupLabel, from, to, publishedThrough, days, memberNames = [] }) {
  const lines = [];
  lines.push(`Meeting days for ${groupLabel || "the group"}, ${fmtDay(from)} to ${fmtDay(to)}${publishedThrough ? ` (dispatch published through ${fmtDay(publishedThrough)})` : ""}`);
  if (memberNames.length) lines.push(`Members: ${memberNames.join(", ")}`);
  lines.push("");
  for (const t of TIERS) {
    const hits = (days || []).filter((d) => d.tier === t.key);
    if (!hits.length) continue;
    lines.push(`${t.label}:`);
    for (const h of hits) {
      let extra = "";
      if (h.tier === "together") extra = ` (${h.port})`;
      else if (h.tier === "all_but_one" && h.holdout) extra = ` (${h.holdout.name} ${h.holdout.label})`;
      else if (h.tier === "workable") {
        const who = h.statuses.filter((s) => s.level === "maybe").map((s) => `${s.name} ${s.label}`);
        extra = who.length ? ` (${who.join("; ")})` : "";
      }
      lines.push(`  ${fmtDay(h.date)}${extra}`);
    }
    lines.push("");
  }
  if ((days || []).length === 0) lines.push("No day in this range works for everyone.");
  return lines.join("\n").trimEnd();
}
